import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
  UntypedFormControl,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { ValidationData } from '../../../../interfaces/validation-data.interface';
import { BackendAccessService } from '../../../../Services/APICalls/backend-access-service';
import { Transfer } from '../../../../interfaces/transfer.interface';
import { ValidationError } from '../../../../interfaces/validation-error.interface';
import { Products } from '../../../../interfaces/products.interface';
import { take } from 'rxjs';
import { SiteIdResponse } from '../../../../interfaces/engage-siteID';
import { LoadingService } from '../../../../Services/Loading/loading-service.service';
import { BranchSelectionService } from '../../../../Services/branch-selection.service';
import { CanActivateService } from '../../../../Services/CanActivate/can-activate.service';

@Component({
  selector: 'app-credits-transfer-page',
  templateUrl: './credits-transfer-page.component.html',
  styleUrls: ['./credits-transfer-page.component.scss'],

  standalone: false,
})
export class CreditsTransferPageComponent implements OnInit {
  static setGroupId(productId: string, productGroupId: string) {
    throw new Error('Method not implemented.');
  }
  currentView: string = 'form';
  form!: UntypedFormGroup;
  transferMessage!: string;
  recipientName!: string;
  products: Products[] = [];
  productId: string = '';
  productGroupId: string = '';
  CreditTransferMain!: object;
  loading = false;
  siteIdDisplay = '';
  accountNumber: string = '';
  accountId: string = '';
  selectedBranchId: string | null = null;
  productsList: Products[] = [];
  @Output() navigateToInventory = new EventEmitter<void>();
  @Output() navigateToHistory = new EventEmitter<void>();
  @Output() navigateToTransfer = new EventEmitter<void>();

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private backendAccessService: BackendAccessService,
    private loadingService: LoadingService,
    private branchSelectionService: BranchSelectionService,
    private canActivate: CanActivateService
  ) {}
  /* istanbul ignore next */
  ngOnInit() {
    this.getProducts();
    this.form = this._formBuilder.group({
      accountType: ['', Validators.required],
      productType: ['', [Validators.required]],
      quantity: ['', [Validators.required]],
      siteId: [''],
      purchaseOrder: [''],
      notes: [''],
      accountNumber: [''],
      accountId: [''],
      email: [''],
    });

    this.form.get('accountType')?.valueChanges.subscribe((checkedValue) => {
      // Clear validators for all optional fields initially
      this.form.get('email')?.clearValidators();
      this.form.get('siteId')?.clearValidators();
      this.form.get('accountId')?.clearValidators();

      if (checkedValue === 'License Management Account') {
        this.form.get('email')?.setValidators(Validators.required);
        this.form.removeControl('siteId');
        this.form.addControl(
          'email',
          new UntypedFormControl('', Validators.required)
        );
        this.siteIdDisplay = '';
      } else if (checkedValue === 'ENGAGE\u2122 Account') {
        this.form.get('siteId')?.setValidators(Validators.required);
        this.form.removeControl('email');
        this.form.removeControl('accountId');
        this.form.addControl(
          'siteId',
          new UntypedFormControl('', Validators.required)
        );
      } else if (checkedValue === 'Company Search') {
        this.form.get('accountId')?.setValidators(Validators.required);
        this.form.removeControl('email');
        this.form.addControl(
          'accountId',
          new UntypedFormControl('', Validators.required)
        );
      }

      // Update validity of the form
      this.form.get('email')?.updateValueAndValidity();
      this.form.get('siteId')?.updateValueAndValidity();
      this.form.get('accountId')?.updateValueAndValidity();
    });

    this.branchSelectionService.selectedBranch$.subscribe(
      (selectedBranchId) => {
        this.selectedBranchId = selectedBranchId;
      }
    );

    this.canActivate.companyProfile$.subscribe((companyProfile) => {
      if (companyProfile) {
        console.log('companyProfile', companyProfile);
        this.branchSelectionService.setCompanyName(companyProfile.companyName);
      }
    });
  }

  /* istanbul ignore next */
  handleNavigateToInventory() {
    this.navigateToInventory.emit();
  }
  /* istanbul ignore next */
  handleNavigateToHistory() {
    this.navigateToHistory.emit();
  }
  /* istanbul ignore next */
  handleNavigateToTransfer() {
    this.navigateToTransfer.emit();
  }

  /* istanbul ignore next */
  getSiteIdDisplay(value: string) {
    if (value === null || value === '') {
      this.siteIdDisplay = '';
      this.form.get('siteId')?.setErrors(null);
    } else {
      this.loadingService.show();
      this.backendAccessService
        .getEngageIdName(value)
        .pipe(take(1))
        .subscribe({
          next: (result: SiteIdResponse) => {
            const siteIdResponse = result.siteIdResponses[0];
            if (siteIdResponse.isValid) {
              this.siteIdDisplay = siteIdResponse.siteName;
              this.form.get('siteId')?.setErrors(null);
            } else {
              this.siteIdDisplay = siteIdResponse.errorDesc;
              this.form.get('siteId')?.setErrors({ invalid: true });
            }
            this.loadingService.hide();
          },
          error: (error) => {
            this.loadingService.hide();
            this.form.get('siteId')?.setErrors({ invalid: true });
          },
          complete: () => this.loadingService.hide(),
        });
    }
  }

  /* istanbul ignore next */
  clearForm() {
    this.form.updateValueAndValidity();
    this.form.reset();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.siteIdDisplay = '';
  }

  setGroupId() {
    if (
      this.productId === '4857b390-d716-4105-a402-3c84bbd3ad9d' ||
      this.productId === '5c7915e4-f59a-4c0a-bbf7-741e4fa1d43f'
    ) {
      this.productGroupId = 'GRP-5-YR';
    } else {
      this.productGroupId = 'GRP-1-YR';
    }
  }
  /* istanbul ignore next */
  private determineProductId(): string {
    if (this.form.controls['productType'].value.id === undefined) {
      switch (this.form.controls['productType'].value) {
        case '1: Object':
          return this.products[0].id;
        case '2: Object':
          return this.products[1].id;
        default:
          console.error('Unknown product type');
          return '';
      }
    } else {
      return this.form.controls['productType'].value.id;
    }
  }
  /* istanbul ignore next */
  private prepareDataToValidate(isSupport: boolean): ValidationData {
    this.productId = this.determineProductId();
    this.setGroupId();

    let dataToValidate: ValidationData = {
      quantity: parseInt(this.form.controls['quantity'].value, 10),
      productId: this.productId,
      productGroupId: this.productGroupId,
      validateOnly: true,
      purchaseOrder: this.form.controls['purchaseOrder'].value,
      notes: this.form.controls['notes'].value,
      branchId: this.selectedBranchId ?? null,
    };

    let validationType: string;
    if (isSupport) {
      validationType =
        this.form.controls['accountType'].value === 'Company Search'
          ? 'account'
          : 'site';
      if (validationType === 'account') {
        dataToValidate.accountId =
          this.form.controls['accountId'].value.accountId;
        dataToValidate.accountNumber =
          this.form.controls['accountNumber'].value.accountNumber;
      } else {
        dataToValidate.siteId = this.form.controls['siteId'].value.toString();
      }
    } else {
      validationType =
        this.form.controls['accountType'].value === 'License Management Account'
          ? 'account'
          : 'site';
      if (validationType === 'account') {
        dataToValidate.email = this.form.controls['email'].value;
      } else {
        dataToValidate.siteId = this.form.controls['siteId'].value.toString();
      }
    }

    return dataToValidate;
  }
  /* istanbul ignore next */
  private handleApiResponse(response: any) {
    console.log('Transfer response:', response);
    if (response.errors?.errorDetails) {
      this.handleValidationError(response.errors.errorDetails);
    } else {
      this.recipientName = response;
      this.viewToggler();
    }
  }
  /* istanbul ignore next */
  private handleApiError(error: any) {
    console.error('Transfer error:', error);
    this.loadingService.hide();
    if (
      error.status === 400 &&
      error.error.errors &&
      error.error.errors.errorDetails
    ) {
      this.handleValidationError(error.error.errors.errorDetails);
    }
  }
  /* istanbul ignore next */
  validateData() {
    this.loadingService.show();
    const dataToValidate = this.prepareDataToValidate(false);
    const validationType =
      this.form.controls['accountType'].value === 'License Management Account'
        ? 'account'
        : 'site';

    this.backendAccessService
      .creditTransferPost(dataToValidate, validationType)
      .subscribe({
        next: (response) => this.handleApiResponse(response),
        error: (error) => this.handleApiError(error),
        complete: () => this.loadingService.hide(),
      });
  }
  /* istanbul ignore next */
  validateSupportData() {
    this.loadingService.show();
    const dataToValidate = this.prepareDataToValidate(true);
    const validationType =
      this.form.controls['accountType'].value === 'Company Search'
        ? 'account'
        : 'site';

    this.backendAccessService
      .creditSupportTransferPost(dataToValidate, validationType)
      .subscribe({
        next: (response) => this.handleApiResponse(response),
        error: (error) => this.handleApiError(error),
        complete: () => this.loadingService.hide(),
      });
  }

  /* istanbul ignore next */
  getProducts() {
    this.loadingService.show();
    this.backendAccessService
      .getProducts()
      .subscribe((response: Products[]) => {
        this.productsList = this.sortProductsByDuration(response);
        this.loadingService.hide();
      });
  }
  /* istanbul ignore next */
  sortProductsByDuration(products: Products[]): Products[] {
    return products.sort((a, b) =>
      a.duration > b.duration ? 1 : b.duration > a.duration ? -1 : 0
    );
  }

  /* istanbul ignore next */
  viewToggler(): void {
    if (this.currentView === 'form') {
      this.currentView = 'review';
    } else {
      this.transferMessage = '';
      this.currentView = 'form';
    }
  }
  /* istanbul ignore next */
  submitTransfer() {
    this.setGroupId();
    this.loadingService.show();
    let postData: Transfer = {
      productId: this.productId,
      productGroupId: this.productGroupId,
      quantity: parseInt(this.form.controls['quantity'].value),
      validateOnly: false,
      purchaseOrder: this.form.controls['purchaseOrder'].value,
      notes: this.form.controls['notes'].value,
      branchId: this.selectedBranchId,
    };
    let transferType =
      this.form.controls['accountType'].value === 'License Management Account'
        ? 'account'
        : 'site';
    postData =
      transferType === 'account'
        ? { ...postData, email: this.form.controls['email'].value }
        : {
            ...postData,
            siteId: this.form.controls['siteId'].value.toString(),
          };
    this.backendAccessService
      .creditTransferPost(postData, transferType)
      .subscribe({
        next: (event) => this.handleTransferResponse(event),
        // on errors
        error: (error) => {
          this.handleTransferResponse(error.error, error.status);
          this.loadingService.hide();
        },
        complete: () => this.loadingService.hide(),
      });
  }
  /* istanbul ignore next */
  submitSupportTransfer() {
    this.setGroupId();
    this.loadingService.show();
    let postData: Transfer = {
      productId: this.productId,
      productGroupId: this.productGroupId,
      quantity: parseInt(this.form.controls['quantity'].value),
      validateOnly: false,
      purchaseOrder: this.form.controls['purchaseOrder'].value,
      notes: this.form.controls['notes'].value,
    };
    let transferType =
      this.form.controls['accountType'].value === 'Company Search'
        ? 'account'
        : 'site';
    postData =
      transferType === 'account'
        ? {
            ...postData,
            accountId: this.form.controls['accountId'].value.accountId,
            accountNumber:
              this.form.controls['accountNumber'].value.accountNumber,
          }
        : {
            ...postData,
            siteId: this.form.controls['siteId'].value.toString(),
          };
    this.backendAccessService
      .creditSupportTransferPost(postData, transferType)
      .subscribe({
        next: (event) => this.handleTransferResponse(event),
        // on errors
        error: (error) => {
          this.handleTransferResponse(error.error, error.status);
          this.loadingService.hide();
        },
        // called once on completion
        complete: () => this.loadingService.hide(),
      });
  }
  /* istanbul ignore next */
  handleValidationError(errors: ValidationError[]) {
    console.log('Handling validation errors:', errors);

    errors.forEach((error: ValidationError) => {
      let formControl = this.form.get(error.field);

      // Specific handling for "Invalid Company Account"
      if (
        error.field === 'account' &&
        error.message === 'Invalid Company Account'
      ) {
        formControl = this.form.get('accountId');
        if (formControl) {
          formControl.setErrors({
            invalid: true,
            message: 'Invalid company account. Please check and try again.',
          });
          console.log(
            `Set specific error on field accountId: Invalid company account.`
          );
        }
      } else {
        // General error handling
        if (formControl) {
          formControl.setErrors({
            invalid: true,
            message: error.message,
          });
          console.log(`Set error on field ${error.field}: ${error.message}`);
        }
      }
    });

    this.form.updateValueAndValidity();
  }

  /* istanbul ignore next */
  handleTransferResponse(response: any, status?: number) {
    console.log('Handling transfer response:', response);
    if (status && status >= 400) {
      this.transferMessage = 'failure';

      if (response.message) {
        console.error(`Error: ${response.message}`);
      }
    } else if (response.errors == null) {
      this.transferMessage = 'success';
    } else {
      this.transferMessage = 'failure';

      if (response.errors.errorDetails) {
        response.errors.errorDetails.forEach((errorDetail: any) => {
          const formControl = this.form.get(errorDetail.field);
          if (formControl) {
            formControl.setErrors({
              invalid: true,
              message: errorDetail.message,
            });
          }
        });
      } else {
        // Handle other types of errors if necessary
      }
    }
  }
}

/* istanbul ignore next */
export function invalidSelectionValidator(selection: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    let product: Products = control.value;

    if (product.duration === selection) {
      return control.value;
    } else {
      return null;
    }
  };
}
/* istanbul ignore next */
export function sameEmailValidator(sameEmail: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    let email = control.value;

    if (email === sameEmail) {
      return { langSection: 'TransferForm', langText: 'ErrorSameEmail' };
    } else {
      return null;
    }
  };
}
